import { useAuthContext } from '@dbg-ui/auth';
import { AccountCircle, LockOpen, Token } from '@mui/icons-material';
import { Button, CircularProgress, Divider, Grid, Popover, Typography } from '@mui/material';
import {makeStyles} from '@mui/styles';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ApiKeys from '../pages/ApiKeys';

const useStyles = makeStyles(() => ({
  accountButton: {
    '& .MuiSvgIcon-root': {
      fontSize: 40,
      padding: 2
    }
  },
  accountTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1.2
  },
  accountEmail: {
    fontWeight: 500,
    fontSize: 14
  },
  paper: {
    width: ({popupWidth}) => popupWidth,
    borderRadius: 8,
    marginLeft: 50,
    marginTop: 5
  },
  popupButton: {
    width: '100%',
    justifyContent: 'flex-start'
  },
}));

function Account() {
  const authContext = useAuthContext();
  const currentUser = useSelector(state => state.user.currentUser);
  const currentUserError = useSelector(state => state.user.currentUserError);
  const ref = useRef(null);
  const emailRef = useRef(null);
  const {t} = useTranslation();

  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [openApiKeys, setOpenApiKeys] = useState(false);
  const popupWidth = emailRef.current ? Math.ceil(emailRef.current.offsetWidth) : undefined;
  const classes = useStyles({popupWidth});

  const handleOpenApiKeys = () => {
    setOpenApiKeys(true);
    setOpenProfileMenu(false);
  };

  const handleCloseApiKeys = () => {
    setOpenApiKeys(false);
  };

  if (!authContext.isAuthenticated) {
    return (
      <Button startIcon={<LockOpen />} variant='text' color='inherit' onClick={authContext.requestAuth}>
        {t('account.text.login')}
      </Button>
    );
  }

  if (currentUserError) {
    return (
      <Typography component='span' variant='body2' color='inherit'>
        {t('account.text.unavailable')}
      </Typography>
    );
  }

  if (!currentUser?.email) {
    return <CircularProgress color='inherit' size={24} aria-label={t('account.text.loading')} />;
  }

  const givenName = currentUser.name || authContext.user?.given_name || '';
  const familyName = currentUser.surname || authContext.user?.family_name || '';
  const displayName = `${givenName} ${familyName}`.trim() || currentUser.email;

  return (<>
    <Button
      startIcon={<AccountCircle />}
      variant='text'
      color='inherit'
      ref={ref}
      onClick={()=>setOpenProfileMenu(true)}
      className={classes.accountButton}
    >
      <span className={classes.accountTextWrap}>
        <span>{displayName}</span>
        <span className={classes.accountEmail} ref={emailRef}>{currentUser.email}</span>
      </span>
    </Button>
    <Popover
      anchorEl={ref.current}
      onClose={() => setOpenProfileMenu(false)}
      open={openProfileMenu}
      fullWidth
      className={classes.popupButton}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      PaperProps={{
        className: classes.paper
      }}
      aria-hidden={!openProfileMenu}>
      <Grid container direction='column' spacing={1}>
        <Grid item>
          <Button 
            startIcon={<Token/>}
            variant='text' 
            color='inherit'
            fullWidth
            className={classes.popupButton}
            onClick={handleOpenApiKeys}>
            {t('account.text.apiKeys')}
          </Button>
        </Grid>
        <Divider />
        <Grid item>
          <Button
            startIcon={<LockOpen />}
            variant='text' 
            color='inherit'
            fullWidth
            className={classes.popupButton}
            onClick={authContext.logoutWithRedirect}>
            {t('account.text.logout')}
          </Button>
        </Grid>
      </Grid>
    </Popover>
    <ApiKeys open={openApiKeys} handleClose={handleCloseApiKeys} />
  </>);
}

export default Account;
